import type { Article } from "../../data/articles";
import { formatDate } from "../../data/articles";
import { AppLink } from "../AppLink";
import { Icon } from "../Icon";
import { Picture } from "../Picture";

/** Journal essay card (journal grid and "further reading"). The whole card is one link via the title's ::after. */
export function ArticleCard({
  article: a,
  className = "",
}: {
  article: Article;
  className?: string;
}) {
  return (
    <article
      className={`group relative flex flex-col bg-surface-ivory rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 ${className}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-surface-dim">
        <Picture
          name={a.image}
          alt={a.imageAlt}
          sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
          className="block w-full h-full"
          imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 bg-botanical-dark/80 backdrop-blur-sm text-gold-light text-label-sm font-label-sm uppercase px-2.5 py-1 rounded">
          {a.category}
        </div>
        <div className="absolute bottom-4 right-4 bg-surface-ivory/90 backdrop-blur-md px-2 py-0.5 rounded text-primary font-label-sm text-label-sm">
          {a.readMinutes} min read
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1 justify-between">
        <div>
          <div className="flex items-center gap-space-xs text-outline font-label-sm text-label-sm uppercase mb-2">
            <span>Issue #{a.issue}</span>
            <span aria-hidden="true">•</span>
            <time dateTime={a.date}>{formatDate(a.date)}</time>
          </div>
          <h3 className="font-headline-sm text-headline-sm text-primary group-hover:text-emerald-vivid transition-colors line-clamp-2">
            <AppLink
              href={`/journal/${a.slug}`}
              className="after:absolute after:inset-0 after:z-10"
            >
              {a.title}
            </AppLink>
          </h3>
          <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs line-clamp-3 leading-relaxed">
            {a.excerpt}
          </p>
        </div>
        <div className="pt-6 mt-6 border-t border-surface-container-high flex items-center justify-between">
          <span className="font-label-sm text-label-sm text-secondary font-medium">
            {a.topic}
          </span>
          <span
            aria-hidden="true"
            className="inline-flex items-center gap-1 font-label-md text-label-md uppercase tracking-wider text-primary group-hover:text-emerald-vivid"
          >
            Read{" "}
            <Icon
              name="arrow_forward"
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </span>
        </div>
      </div>
    </article>
  );
}
